'use client';

import { useState } from "react";
import Swal from "sweetalert2";
import { uploadToS3, submitPDPLRequest } from '@/apis';

export default function AccessRequestPage() {
	const [data, setData] = useState({
		fullName: "",
		dob: "",
		email: "",
		mobile: "",
		customerId: "",
		file: null,
		requestTypes: [],
		description: ""
	});

	const [identityFile, setIdentityFile] = useState(null);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;

		if (type === "checkbox") {
			setData((prev) => {
				const newArray = checked
					? [...prev.requestTypes, value]
					: prev.requestTypes.filter((v) => v !== value);
				return { ...prev, requestTypes: newArray };
			});
		} else if (type === "file") {
			setData((prev) => ({ ...prev, file: e.target.files[0] }));
		} else {
			setData((prev) => ({ ...prev, [name]: value }));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!data.file) {
			return Swal.fire("Error", "Please upload a verification document.", "error");
		}

		const formData = new FormData();
		formData.append("file", data.file);

		const res = await uploadToS3(formData);

		setIdentityFile({ name: data.file.name, url: res.fileUrl });

		const payload = {
			formType: "PDAR", // Personal Data Access Request
			applicantInformation: {
				fullName: data.fullName,
				email: data.email,
				phone: data.mobile,
				dob: data.dob,
				customerId: data.customerId,
			},
			identityDocuments: identityFile ? [identityFile] : [],
			supportingDocuments: [], // add if you allow multiple files
			dataToBeRectified: [], // not used here, keep empty
			accessRequestTypes: data.requestTypes, // selected checkboxes as reasons
			requestDescription: data.description,
		};

		try {
			console.log(payload);
			const res = await submitPDPLRequest(payload);

			Swal.fire({
				icon: "success",
				title: "Request Submitted",
				html: `${res.message}<br>Case ID: <b style="color:blue">${res.caseId}</b>`,
				timer: 6000,
			});
		} catch (err) {
			console.error(err);
			Swal.fire("Error", "Something went wrong.", "error");
		}
    };


	return (
		<section className='legacy-page bg-gray-50 text-gray-900 px-6 py-6 md:py-10'>
			<div className='container mx-auto max-w-4xl lg:max-w-5xl'>
				<form className='space-y-8' onSubmit={handleSubmit}>
					{/* Header */}
					<h1 className='text-3xl md:text-4xl font-bold text-primary mb-8'>
						Personal Data Access Request (PDPL)
					</h1>

					{/* A. User Identification Fields */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>
							A. User Identification Fields
						</h2>
						<p className='text-gray-600 mb-4'>
							PDPL requires reasonable measures to verify identity.
						</p>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>
									Full Name <span className='text-red-500'>*</span>
								</label>
								<input
									name="fullName"
									value={data.fullName}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder="Full Name (as per Emirates ID/Passport) *"
									required
								/>
							</div>

							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>Date of Birth</label>
								<input
									type='date'
									name="dob"
									value={data.dob}
									onChange={handleChange}
									className='border p-2 rounded text-gray-700'
								/>
							</div>

							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>
									Registered Email Address <span className='text-red-500'>*</span>
								</label>
								<input
									type="email"
									name="email"
									value={data.email}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder="Registered Email Address Linked to Account"
									required
								/>
							</div>

							<div className='flex flex-col'>
								<label className='mb-1 font-medium'>
									Registered Mobile Number <span className='text-red-500'>*</span>
								</label>
								<input
									name="mobile"
									value={data.mobile}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder="Registered Mobile Number Linked to Account"
									required
								/>
							</div>

							<div className='flex flex-col md:col-span-2'>
								<label className='mb-1 font-medium'>
									Customer ID / Account ID
								</label>
								<input
									name="customerId"
									value={data.customerId}
									onChange={handleChange}
									className='border p-2 rounded'
									placeholder="Optional"
								/>
							</div>
						</div>
					</section>

					{/* B. Verification Document Upload */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>
							B. Verification Document Upload
						</h2>
						<p className='text-gray-700 mb-3'>
							Upload <b> one mandatory </b> document for identity verification.
						</p>
						<ul className='list-disc list-inside text-gray-700 mb-4'>
							<li>Emirates ID (Front + Back)</li>
							<li>OR Passport Photo Page</li>
							<li>Allowed: JPG, PNG, PDF — Max size: 5MB</li>
						</ul>
						<label className='inline-block bg-primary text-white px-4 py-2 rounded cursor-pointer'>
							Upload Document *
							<input
								type='file'
								name="file"
								className='hidden'
								accept='.jpg,.jpeg,.png,.pdf'
								onChange={handleChange}
								required
							/>
						</label>
						{data.file && <p className="mt-2 text-red-500">{data.file.name}</p>}
					</section>

					{/* C. Type of Access Request */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>
							C. Type of Access Request
						</h2>
						<p className='text-gray-700 mb-3'>
							Select what you want access to:
						</p>

						<div className='grid grid-cols-1 gap-2'>
							{[
								'Copy of all personal data stored',
								'Confirmation of whether data is being processed',
								'Categories of data processed',
								'Purpose of processing',
								'Third parties data has been shared with',
								'Source of data (if not collected from user)',
								'Automated decision-making information (if any)',
							].map((item, i) => (
								<label key={i} className='flex items-center gap-2'>
									<input
										type='checkbox'
										value={item}
										checked={data.requestTypes.includes(item)}
										onChange={handleChange}
										className='w-4 h-4'
									/>
									{item}
								</label>
							))}
						</div>
					</section>

					{/* D. Additional Request Description */}
					<section className='mb-8'>
						<h2 className='text-lg font-semibold mb-2'>
							D. Additional Request Description
						</h2>
						<textarea
							name="description"
							value={data.description}
							onChange={handleChange}
							className='border p-2 rounded w-full'
							rows={4}
							placeholder='Describe your request in detail'
						/>
					</section>

					{/* Submission Buttons */}
					<div className='flex gap-4 mt-6'>
						<button
							type="submit"
							className='bg-primary text-white px-6 py-2 rounded-md'
						>
							Submit Access Request
						</button>
						<button
							type="button"
							className='bg-gray-300 px-6 py-2 rounded-md'
							onClick={() => window.location.reload()}
						>
							Cancel
						</button>
					</div>
				</form>
			</div>
		</section>
	);
}
